import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  IconButton,
  Container,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
} from "@mui/material";
import { motion } from "framer-motion";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AI from "../../assets/AI.jpg";
import Ethics from "../../assets/ethics.jpg";

const policyContent = {
  AI: {
    title:
      "Standards for the Use of AI at the Mississippi Digital Humanities Hub",
    sections: [
      {
        subHeading: "Preamble",
        text: "New tools to assist with the preserving, mapping, and analyzing of data, images, and texts (the foundation of digital humanities research) are being developed constantly and many employ the use of artificial intelligence (AI). These tools make the power of the digital humanities more accessible to many, but the rapidly evolving landscape presents certain risks when relying heavily on AI tools. The Center for the Digital Humanities and the Mississippi Digital Humanities Hub at Southern Miss recommends these guidelines for the use of AI for digital humanities research while recognizing that the field is evolving and these guidelines will have to evolve as well.",
      },
      {
        subHeading: "Transparency",
        text: "All digital humanities undertakings should acknowledge the tools that were used and, when possible, make the data sets analyzed freely available to researchers.",
      },
      {
        subHeading: "Citation and Copyright",
        text: "Researchers using AI have an affirmative responsibility to acknowledge the sources they use for their research. If access to that information is not provided by the AI tool and fully citing sources or respecting copyright is not possible, the tool should not be used.",
      },
      {
        subHeading: "Acknowledgement",
        text: "When using AI digital humanities tools, extra care should be taken to describe how the tool works and any external inputs (including data sets used to train the AI tool) that have contributed to the research results. AI tools are constantly learning and changing, so it is not always possible for researchers to reproduce results at a later date. Care should be used to describe the research process as thoroughly as possible to allow future researchers the best opportunity to approximate results.",
      },
      {
        subHeading: "Intentionality",
        text: "When using digital humanities tools, the researcher should continually focus on the purpose and message of the project over the novelty of the tools. The Digital Humanities are as much a method of thinking and research as a means of producing work products. When thinking about incorporating AI into one’s work, researchers should think carefully about what may be lost and whether AI is the best analytical tool or just the easiest.",
      },
      {
        subHeading: "Privacy",
        text: "Researchers using AI should take every precaution to ensure that privileged data is not shared with AI tools and that the privacy of individuals, both contemporary and historical, is respected. Users of AI should be aware that some AI tools collect data and retain the right to use that data.",
      },
      {
        subHeading: "Limitations",
        text: "Statistical analysis, whether generated by AI or more traditional methods, extrapolates results from data.  Researchers have an affirmative responsibility to acknowledge the degree of certainty of the results and the limits of any statistical model to describe the past or predict the future.",
      },
      {
        subHeading: "Labor",
        text: "AI can make humans more productive but was created and continues to exploit human labor.  Researchers should develop policies that reward human labor including employment policies that reward ingenuity and do not impose financial penalties for increased productivity.",
      },
      {
        subHeading: "Environment",
        text: "Currently, AI generated results consume considerably more energy than alternatives. When choosing an approach, researchers may wish to consider the hidden costs of using AI.",
      },
      {
        subHeading: "Images",
        text: "Like AI-generated text and AI-generated data, AI images create ethical dilemmas, including the lack of remuneration for artists whose art served as models, documented issues with bias and stereotyping by AI-generated images, and the possibility of falsified images (e.g. deepfakes) being interpreted as real. Researchers should foreground the ethical concerns of artists and audiences in any creation, development, and/or use of AI-generated images.",
      },
    ],
  },
  Ethics: {
    title: "Acknowledgements Policy",
    generalContent: [
      {
        text: "One of the primary purposes of the Center for Digital Humanities and the Mississippi Digital Humanities Hub at The University of Southern Mississippi is to promote and support the use of digital humanities across the state of Mississippi.",
      },
      {
        text: "In instances where the Mississippi Digital Humanities Hub provides extensive support assistance on a project, it is best practice for the individual or institution to credit the “Hub”  and its members’ contributions. “Extensive assistance” may include, but is not limited to:",
        list: [
          "Extensive research consultations, either in-person, via phone calls, and/or email",
          "Significant amount of time spent working on a project",
          "“Hub” involvement throughout several steps of the project",
          "“Hub” created visualizations and/or text for a project",
          "Financial support or in-kind support",
        ],
      },
      {
        text: "The “Hub” recommends that those supported by the “Hub” name or display our logo on their site. In addition, we suggest: ",
        list: [
          "Listing faculty, staff, and/or student assistants as authors on the specific page or visualization they created",
          "Listing faculty, staff, and/or student assistants in a list of contributors or credits section",
        ],
      },
      {
        text: "Additionally, the “Hub” will list the project on our projects page, along with the name of the individual or institution, a logo or associated image, project name, and short description of the project.  When possible, we would like to mirror the project or link to projects that involved significant collaboration with the “Hub”.",
      },
    ],
  },
};

const Policies = () => {
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [expandedPanels, setExpandedPanels] = useState({});

  useEffect(() => {
    document.title = "Policies - MS Digital Hub";
  }, []);

  const handlePolicyClick = (policyKey) => {
    setSelectedPolicy(policyKey);
    setExpandedPanels({});
  };

  const handleExpandAll = () => {
    if (!selectedPolicy) return;
    const allExpanded = {};
    policyContent[selectedPolicy].sections.forEach((_, index) => {
      allExpanded[index] = true;
    });
    setExpandedPanels(allExpanded);
  };

  const handleCollapseAll = () => {
    setExpandedPanels({});
  };

  const handleAccordionChange = (index) => {
    setExpandedPanels((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };
  const allExpanded =
    selectedPolicy && policyContent[selectedPolicy].sections
      ? policyContent[selectedPolicy].sections.every(
          (_, idx) => expandedPanels[idx]
        )
      : false;
  const allCollapsed =
    selectedPolicy && policyContent[selectedPolicy].sections
      ? policyContent[selectedPolicy].sections.every(
          (_, idx) => !expandedPanels[idx]
        )
      : true;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 1 }}
    >
      <Box
        sx={{
          width: "auto",
          padding: { xs: "20px", sm: "30px", md: "1px 80px" },
          margin: "0px auto",
          backgroundColor: "#ffffff",
          borderRadius: "8px",
          color: "#000",
        }}
      >
        <Typography
          variant="h4"
          sx={{ marginBottom: "30px", textAlign: "center" }}
        >
          Policies and Manuals
        </Typography>

        <Typography
          variant="body1"
          sx={{ marginBottom: "30px", lineHeight: "1.8", textAlign: "center" }}
        >
          The <strong>Mississippi Digital Humanities Hub</strong> provides
          training and resources to Mississippians seeking to digitize and
          provide public access to historical and cultural resources.
        </Typography>
        <Typography
          variant="body1"
          sx={{ color: "gray", mb: 3, textAlign: "center" }}
        >
          Click on a policy to view its details.
        </Typography>

        <Box
          sx={{
            display: "flex",
            gap: "20px",
            justifyContent: "center",
            mb: 3,
          }}
        >
          {Object.keys(policyContent).map((policyKey) => (
            <Card
              key={policyKey}
              sx={{
                p: 2,
                borderRadius: 2,
                boxShadow: selectedPolicy === policyKey ? 6 : 3,
                width: "220px",
                textAlign: "center",
                transition:
                  "transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out",
                bgcolor: selectedPolicy === policyKey ? "#fffbe7" : "#ffffff",
                "&:hover": { transform: "scale(1.05)", bgcolor: "#fffbe7" },
                cursor: "pointer",
              }}
              onClick={() => handlePolicyClick(policyKey)}
            >
              <CardContent>
                <IconButton
                  sx={{
                    borderRadius: 0,
                    padding: 0,
                    background: "transparent",
                    "&:hover": {
                      backgroundColor: "transparent",
                    },
                  }}
                >
                  <Box
                    component="img"
                    src={policyKey === "AI" ? AI : Ethics}
                    alt={`${policyKey} Icon`}
                    sx={{
                      width: 80,
                      height: 80,
                      transition: "transform 0.3s ease-in-out",
                      "&:hover": { transform: "scale(1.1)" },
                    }}
                  />
                </IconButton>
                <Typography variant="h6" sx={{ mt: 2 }}>
                  {policyKey === "AI" ? "AI Policy" : "Acknowledgements Policy"}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>

        {selectedPolicy && policyContent[selectedPolicy].sections && (
          <Box sx={{ textAlign: "center", marginBottom: 2 }}>
            <Button
              variant="outlined"
              onClick={handleExpandAll}
              disabled={allExpanded}
              sx={{
                marginTop: 1,
                bottom: "10px",
                mx: 1,
                "&:hover": { bgcolor: "#fffbe7" },
              }}
            >
              Expand All
            </Button>
            <Button
              variant="outlined"
              onClick={handleCollapseAll}
              disabled={allCollapsed}
              sx={{
                marginTop: 1,
                bottom: "10px",
                mx: 1,
                "&:hover": { bgcolor: "#fffbe7" },
              }}
            >
              Collapse All
            </Button>
          </Box>
        )}

        {selectedPolicy && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h5" sx={{ mb: 4, textAlign: "center" }}>
              {policyContent[selectedPolicy].title}
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: "gray", mb: 3, textAlign: "center" }}
            >
              {policyContent[selectedPolicy].description}
            </Typography>
            <Box sx={{ width: "80%", margin: "0 auto" }}>
              {policyContent[selectedPolicy].sections
                ? policyContent[selectedPolicy].sections.map((section, idx) => (
                    <Accordion
                      key={idx}
                      expanded={!!expandedPanels[idx]}
                      onChange={() => handleAccordionChange(idx)}
                      sx={{
                        boxShadow: 0,
                        borderBottom: "1px solid #ccc",
                        mb: 1,
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                          cursor: "pointer",
                          "&:hover": { textDecoration: "underline" },
                        }}
                      >
                        <Typography variant="body1">
                          {section.subHeading}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{}}>
                        <Typography variant="body2">{section.text}</Typography>
                      </AccordionDetails>
                    </Accordion>
                  ))
                : policyContent[selectedPolicy].generalContent.map(
                    (content, idx) => (
                      <Box
                        key={idx}
                        sx={{ padding: { xs: "5px", sm: "10px" }, mb: 2 }}
                      >
                        <Typography variant="body1">{content.text}</Typography>
                        {content.list && (
                          <ul>
                            {content.list.map((point, i) => (
                              <li key={i}>
                                <Typography variant="body1">{point}</Typography>
                              </li>
                            ))}
                          </ul>
                        )}
                      </Box>
                    )
                  )}
            </Box>
          </Box>
        )}
      </Box>
    </motion.div>
  );
};
export default Policies;
